/**
 * Relative-time helpers — single source of truth for the "X secondi
 * fa" / "X minuti fa" / "X ore fa" formatting used by every banner,
 * chip, and toast that displays past-event timestamps.
 *
 * Lives in ``packages/ui/src/lib`` so primitives in ``@ando/ui`` can
 * import without dragging the larger composer/editor surfaces.
 *
 * Locale is parameterised so the same primitive serves the Italian
 * IT chrome AND any future EN tier without forking. Default ``it``.
 */

export type RelativeTimeLocale = "it" | "en";

interface RelativeStrings {
  readonly seconds: (n: number) => string;
  readonly minutes: (n: number) => string;
  readonly hours: (n: number) => string;
  readonly days: (n: number) => string;
  readonly justNow: string;
}

const STRINGS: Record<RelativeTimeLocale, RelativeStrings> = {
  it: {
    seconds: (n) => `${n}s fa`,
    minutes: (n) => `${n} min fa`,
    hours: (n) => `${n} h fa`,
    days: (n) => `${n} g fa`,
    justNow: "pochi istanti fa",
  },
  en: {
    seconds: (n) => `${n}s ago`,
    minutes: (n) => `${n} min ago`,
    hours: (n) => `${n} h ago`,
    days: (n) => `${n} d ago`,
    justNow: "just now",
  },
};

export interface FormatRelativePastOptions {
  readonly locale?: RelativeTimeLocale;
  /**
   * What to return when the input timestamp can't be parsed (empty,
   * malformed, or NaN). Default ``null`` lets the caller render a
   * domain-specific fallback (e.g. "pochi istanti fa" when the event
   * was just observed but the timestamp didn't ride the wire).
   */
  readonly fallback?: string | null;
}

/**
 * Format an ISO timestamp as a relative past expression ("X min fa").
 *
 * Ladder: under 10 s → "pochi istanti fa" (avoids the staccato "1s fa"
 * / "2s fa" jitter on freshly-emitted events). 10–59 s → "{n}s fa".
 * 1–59 min → "{n} min fa". 1–23 h → "{n} h fa". ≥ 1 d → "{n} g fa".
 */
export function formatRelativePast(
  iso: string | null | undefined,
  options: FormatRelativePastOptions = {},
): string | null {
  const { locale = "it", fallback = null } = options;
  const strings = STRINGS[locale];
  if (!iso) return fallback;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return fallback;
  const elapsed = Math.max(0, Date.now() - ms);
  const seconds = Math.floor(elapsed / 1000);
  if (seconds < 10) return strings.justNow;
  if (seconds < 60) return strings.seconds(seconds);
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return strings.minutes(minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return strings.hours(hours);
  const days = Math.floor(hours / 24);
  return strings.days(days);
}
